import { useState, useEffect, useCallback } from "react";
import Typography from "apollo-react/components/Typography";
import makeStyles from '@mui/styles/makeStyles';
import Tag from 'apollo-react/components/Tag';
import UserIcon from 'apollo-react-icons/User';
import ConversationChat from 'apollo-react-icons/ConversationChat';
import Avatar from 'apollo-react/components/Avatar';

import { ROLES } from "../../../constants/roles";
import { CATEGORIES } from "../../../constants/categories";
import { STATUSES } from "../../../constants/statuses";
import axiosInstance from "../../../axiosInstance";
import CommentToggle from "../CommentToggle/CommentToggle";
import CommentsList from '../FormApproveComponents/CommentsList/CommentsList';

const styles = {
    commentButton: {
        border: '1px solid #0557D5 !important',
        padding: '8px 16px !important',
        fontFamily: `"Proxima Nova", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif !important`,
        fontSize: '14px !important',
        fontWeight: '500 !important',
        lineHeight: '1.5 !important',
        borderRadius: '4px !important',
    },
    commentButtonContainer: {
        display: 'flex',
        justifyContent: 'flex-end',
    },
    profileSection: {
        display: 'flex',
        flexDirection: 'row',
        gap: 16,
        border: '1px solid #E6E6E6',
        padding: '16px 48px 16px 24px',
        borderRadius: '8px'
    },
    profileAvatar: {
        backgroundColor: '#0557D5 !important',
        color: '#fff'
    },
    noItemsContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        marginTop: 32
    },
    noItemsContainerContent: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
    },
    noItemsContainerIcon: {
        width: 74,
        height: 74,
        color: '#EBEBEB'
    },
    commentsSectionContainer: {
        marginTop: '35px'
    },
};

const useStyles = makeStyles(styles);

const CommitteeECTDSection = ({ section = null, role = '', ectdFileId = null, requestId = null, containerClassName = '',
    committeeReviewRequestStatus = '', currentUser = null,
    requestProgressId = null,
}) => {
    const classes = useStyles();
    const [comments, setComments] = useState([]);

    const fetchCommentsForSection = useCallback(async () => {
        if (!section?.id) return;
        if (!ectdFileId) return;
        let allComments = [];
        const getCommentsLink = (role === "customer" || CATEGORIES.COMMITTEE_CATEGORIES.includes(role)) ? "enduser/" : "";
        const response = await axiosInstance.get(`/api/ectd-comments/${getCommentsLink}${ectdFileId}`, {
            params: {
                end_user_id: section?.id,
            }
        });
        allComments = response.data || [];
        if (requestId && CATEGORIES.ECTD_ASSESSMENT_CATEGORIES.includes(role) && section?.id === currentUser?.id) {
            const responseCoreComments = await axiosInstance.get(`/api/core-validator-directed-committee-comments/core-comments-for-committee/${requestId}`);
            allComments = allComments.concat(responseCoreComments.data.map(c => {
                return {
                    ...c,
                    additional_file: c.uploaded_file || null,
                    additional_file_id: c.uploaded_file_id || null,
                    createdAt: c.createdAt,
                    updatedAt: c.updatedAt,
                    user: c.User || null,
                    comment: c.comment,
                    id: c.id,
                }
            }) || []);
        }
        setComments(allComments);
    }, [ectdFileId, role, section?.id, requestId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        fetchCommentsForSection();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [section]);

    const handleAddCommentFromCoreValidator = async (comment, subFormId, fileId = null, fileData = null) => {
        try {
            // Create FormData for file upload
            const formData = new FormData();
            formData.append("committee_member_id", section?.id);
            formData.append("request_progress_id", requestProgressId);
            formData.append("comment", comment);

            if (fileId) {
                formData.append("file_id", fileId);
            }

            const response = await axiosInstance.post(
                "/api/core-validator-directed-committee-comments/core",
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                }
            );

        } catch (error) {
            console.error("Error adding comment:", error);
            throw error;
        }

    }

    const handleAddComment = async (comment, subFormId, fileId = null, fileData = null) => {
        try {
            const addCommentLink = (role === "customer" || CATEGORIES.COMMITTEE_CATEGORIES.includes(role)) ? "enduser" : "";
            const requestData = {
                ectdFileId,
                comment,
            };

            // Add file ID if provided
            if (fileId) {
                requestData.fileId = fileId;
            }

            const response = await axiosInstance.post(`/api/ectd-comments/${addCommentLink}`, requestData);

            // Enrich the response with file data if available
            // set isOwner to true since the current user just created this comment
            const enrichedComment = { ...response.data, isOwner: true };
            if (fileData && fileId) {
                // Convert fileData to the expected additional_file format
                enrichedComment.additional_file = {
                    id: fileData.id,
                    original_name: fileData.original_name,
                    file_path: null, // Not provided by upload response
                    file_size: fileData.file_size,
                    mime_type: fileData.mime_type,
                    createdAt: fileData.created_at,
                    updatedAt: fileData.created_at,
                    deletedAt: null,
                    user_id: null, // Will be set by backend
                    end_user_id: null, // Will be set by backend
                    ...fileData // Include any additional fields
                };
            }

            console.log("Comment added with file data:", enrichedComment);
            setComments(prev => [enrichedComment, ...prev]);
        } catch (error) {
            console.error("Error adding comment:", error);
            throw error;
        }
    };

    // handle edit comment
    const handleEditComment = async (commentId, newText, attachmentInfo = {}) => {
        try {
            const editLink = (role === "customer" || CATEGORIES.COMMITTEE_CATEGORIES.includes(role)) ? "enduser" : "";
            const requestData = { comment: newText };
            
            if (attachmentInfo.remove_attachment) {
                requestData.remove_attachment = true;
            } else if (attachmentInfo.new_file_id) {
                requestData.additional_file_id = attachmentInfo.new_file_id;
            }

            await axiosInstance.put(`/api/ectd-comments/${editLink ? editLink + '/' : ''}${commentId}`, requestData);
            fetchCommentsForSection();
        } catch (error) {
            console.error('error editing comment:', error);
            throw error;
        }
    };

    // handle delete comment
    const handleDeleteComment = async (commentId) => {
        try {
            const deleteLink = (role === "customer" || CATEGORIES.COMMITTEE_CATEGORIES.includes(role)) ? "enduser" : "";
            await axiosInstance.delete(`/api/ectd-comments/${deleteLink ? deleteLink + '/' : ''}${commentId}`);
            setComments(prev => prev.filter(c => c.id !== commentId));
        } catch (error) {
            console.error('error deleting comment:', error);
            throw error;
        }
    };

    const userReview = section?.review?.review;
    const username = (section?.first_name && section?.last_name && `${section?.first_name} ${section?.last_name}`) ||
        (section?.username && `${section?.username}`) || '';
    const userEmail = section?.email ? `${section?.email}` : ''
    const userCategory = section?.end_users_category?.name || '';
    const userReviewRejectionReason = section?.review?.rejection_reason || '';

    if (comments.length === 0 && role !== ROLES.core_Validator && section?.id !== currentUser?.id
        && !userReview
    )
        return <></>;

    return (
        <div className={containerClassName}>
            <Typography
                variant="title1"
                component="h2"
                sx={{
                    fontWeight: 500,
                    color: "#333",
                    marginBottom: 3,
                    paddingBottom: 1,
                }}
            >
                {userReview && <Tag label={userReview} variant={userReview === 'approved' ? 'success' : 'danger'}/>}{userCategory && ` `}{userCategory}
            </Typography>

            {username && userEmail &&
                <div className={classes.profileSection}>
                    <Avatar className={classes.profileAvatar} stateless><UserIcon /></Avatar>
                    <div>
                        <Typography variant="subtitle" emphasis="high">
                            {username}
                        </Typography>
                        <Typography variant="caption">
                            {userEmail}
                        </Typography>
                    </div>
                </div>
            }

            {comments.length === 0 &&
                <div className={classes.noItemsContainer}>
                    <div className={classes.noItemsContainerContent}>
                        <ConversationChat className={classes.noItemsContainerIcon} />
                        <Typography variant="subtitle">
                            No comments yet
                        </Typography>
                    </div>
                </div>
            }
            {/* Comments Section*/}
            <div className={classes.commentsSectionContainer}>
                <CommentsList
                    prevComments={comments}
                    pagination={{}}
                    filters={{}}
                    updateFilters={() => { }}
                    persona={role === "customer" || CATEGORIES.COMMITTEE_CATEGORIES.includes(role) ? "customer" : "admin"}
                    showControls={false}
                    showFilters={false}
                    showSorting={false}
                    showPageSize={false}
                    showResultsInfo={false}
                    showPagination={false}
                    showCommentsTitle={false}
                    reviews={userReviewRejectionReason ? [section?.review] : []}
                    onEdit={handleEditComment}
                    onDelete={handleDeleteComment}
                />
                {role === ROLES.core_Validator &&
                    committeeReviewRequestStatus === STATUSES.ROLE_STATUSES[ROLES.committee].ACTIVE &&
                    !userReview
                    &&
                    (
                        <CommentToggle
                            onAddComment={handleAddCommentFromCoreValidator}
                            persona={(role === "customer" || CATEGORIES.COMMITTEE_CATEGORIES.includes(role)) ? "customer" : "admin"}
                            buttonText="Send Comment"
                            // buttonProps={{ variant: "primary"}}
                            hasComments={true}
                            hideCancelButton={false}
                            // buttonContainerProps={{className: classes.commentButtonContainer}}
                            hideTitleSeparator={true}
                            buttonAddCommentText="Send Comment"
                            headerText="Send Comment"
                        />
                    )
                }
                {section?.id === currentUser?.id &&
                    committeeReviewRequestStatus === STATUSES.ROLE_STATUSES[ROLES.committee].ACTIVE
                    &&
                    (
                        <CommentToggle
                            onAddComment={handleAddComment}
                            persona={(role === "customer" || CATEGORIES.COMMITTEE_CATEGORIES.includes(role)) ? "customer" : "admin"}
                            buttonText="Comment"
                            // buttonProps={{ variant: "primary"}}
                            hasComments={true}
                            // buttonContainerProps={{className: classes.commentButtonContainer}}
                            hideTitleSeparator={true}
                            hideCancelButton={false}
                        />
                    )
                }
            </div>
        </div>
    )
}

export default CommitteeECTDSection;